/* Clovia full-screen image viewer. Opens from any [data-zoom-src] element (gallery, details,
   fabric) and steps through the ones currently visible, so it follows the chosen colour.
   Tap or click toggles zoom at that point, drag pans, pinch and wheel zoom, arrows step. */

const MAX = 3;

class MediaZoom extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.img = this.querySelector('[data-zoom-img]');
    this.caption = this.querySelector('[data-zoom-caption]');
    this.indexEl = this.querySelector('[data-zoom-index]');
    this.totalEl = this.querySelector('[data-zoom-total]');
    this.slides = [];
    this.i = 0;
    this.pointers = new Map();
    this.reset();

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-zoom-open], [data-zoom-src] img');
      if (!opener || this.dialog.contains(opener)) return;
      const source = opener.closest('[data-zoom-src]');
      if (!source) return;
      e.preventDefault();
      this.open(source, opener);
    });

    this.querySelector('[data-zoom-close]').addEventListener('click', () => this.dialog.close());
    this.querySelectorAll('[data-zoom-step]').forEach((b) => b.addEventListener('click', () => this.step(Number(b.dataset.zoomStep))));
    this.dialog.addEventListener('close', () => this.trigger?.focus());
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.step(document.dir === 'rtl' ? -1 : 1);
      else if (e.key === 'ArrowLeft') this.step(document.dir === 'rtl' ? 1 : -1);
      else if (e.key === '+' || e.key === '=') this.zoomTo(this.scale + 0.5);
      else if (e.key === '-') this.zoomTo(this.scale - 0.5);
    });

    this.img.addEventListener('pointerdown', (e) => this.down(e));
    this.img.addEventListener('pointermove', (e) => this.move(e));
    ['pointerup', 'pointercancel'].forEach((t) => this.img.addEventListener(t, (e) => this.up(e)));
    this.img.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoomTo(this.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
    }, { passive: false });
  }

  open(source, trigger) {
    this.slides = [...document.querySelectorAll('[data-zoom-src]')].filter((el) => !el.closest('[hidden]'));
    this.i = Math.max(0, this.slides.indexOf(source));
    this.trigger = trigger;
    this.totalEl.textContent = this.slides.length;
    this.toggleAttribute('data-single', this.slides.length < 2);
    this.show();
    this.dialog.showModal();
    this.querySelector('[data-zoom-close]').focus();
  }

  step(dir) {
    if (this.slides.length < 2) return;
    this.i = (this.i + dir + this.slides.length) % this.slides.length;
    this.show();
  }

  show() {
    const el = this.slides[this.i];
    if (!el) return;
    this.reset();
    this.img.src = el.dataset.zoomSrc;
    this.img.alt = el.dataset.zoomAlt || '';
    this.caption.textContent = el.dataset.zoomAlt || '';
    this.indexEl.textContent = this.i + 1;
  }

  reset() {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.apply();
  }

  apply() {
    this.img.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
    this.classList.toggle('is-zoomed', this.scale > 1);
  }

  /* Zoom keeping the point under (cx, cy) still. */
  zoomTo(next, cx, cy) {
    const scale = Math.min(MAX, Math.max(1, next));
    if (scale === 1) return this.reset();
    const r = this.img.getBoundingClientRect();
    const ox = (cx ?? r.left + r.width / 2) - (r.left + r.width / 2);
    const oy = (cy ?? r.top + r.height / 2) - (r.top + r.height / 2);
    const k = scale / this.scale;
    this.x -= ox * (k - 1);
    this.y -= oy * (k - 1);
    this.scale = scale;
    this.clamp();
    this.apply();
  }

  clamp() {
    const w = this.img.offsetWidth * (this.scale - 1) / 2;
    const h = this.img.offsetHeight * (this.scale - 1) / 2;
    this.x = Math.min(w, Math.max(-w, this.x));
    this.y = Math.min(h, Math.max(-h, this.y));
  }

  down(e) {
    this.img.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pointers.size === 1) this.start = { x: e.clientX, y: e.clientY, tx: this.x, ty: this.y, moved: false };
    if (this.pointers.size === 2) this.pinch = { d: this.distance(), s: this.scale };
  }

  move(e) {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pointers.size === 2 && this.pinch) {
      const [a, b] = [...this.pointers.values()];
      this.zoomTo(this.pinch.s * (this.distance() / this.pinch.d), (a.x + b.x) / 2, (a.y + b.y) / 2);
      this.start = null;
      return;
    }
    if (!this.start) return;
    const dx = e.clientX - this.start.x;
    const dy = e.clientY - this.start.y;
    if (Math.abs(dx) + Math.abs(dy) > 6) this.start.moved = true;
    if (this.scale > 1) {
      this.x = this.start.tx + dx;
      this.y = this.start.ty + dy;
      this.clamp();
      this.apply();
    }
  }

  up(e) {
    const s = this.start;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinch = null;
    if (!s || this.pointers.size) return;
    this.start = null;
    const dx = e.clientX - s.x;
    if (!s.moved) this.zoomTo(this.scale > 1 ? 1 : 2.5, e.clientX, e.clientY);
    else if (this.scale === 1 && Math.abs(dx) > 50) this.step((dx < 0 ? 1 : -1) * (document.dir === 'rtl' ? -1 : 1));
  }

  distance() {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y) || 1;
  }
}

customElements.define('media-zoom', MediaZoom);
